import { useCallback } from "react";

import { useNavigate } from "react-router";

import { ExtraCoverages } from "@covertree/new-business-extra-coverages";
import {
  hideLoadingFrame,
  saveExtraCoverages,
  showLoadingFrame,
} from "@covertree/new-business-redux";

import { useAppDispatch } from "@redux/hooks";
import { navigateToPolicyStep, NewPolicyRoutes } from "@routing/paths";
import {
  SavePolicyLevelExtraCoveragesInput,
  SaveUnitLevelExtraCoveragesInput,
} from "@covertree/new-business-graphql";
import { ExtraCoveragesForm } from "@covertree/new-business-forms";

export function ExtraCoveragesStep() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const onLoading = useCallback(() => {
    dispatch(
      showLoadingFrame({
        maxLoadTime: 7,
        messages: ["Finding more discounts", "Generating custom add-ons"],
      })
    );
  }, [dispatch]);

  const onLoadingFinished = useCallback(() => {
    dispatch(hideLoadingFrame());
  }, [dispatch]);

  const onLoadingError = useCallback(() => {
    navigateToPolicyStep(
      navigate,
      NewPolicyRoutes.ERROR,
      NewPolicyRoutes.EXTRA_COVERAGES,
      dispatch
    );
  }, [dispatch, navigate]);

  const onHandleExtraCoveragesSubmitStarted = useCallback(() => {
    dispatch(
      showLoadingFrame({
        messages: [
          "Saving extra coverages",
          "Preparing underwriting questions",
        ],
        maxLoadTime: 7,
      })
    );
  }, [dispatch]);

  const handleExtraCoveragesSubmit = useCallback(
    async (payload: {
      policyLevelExtraCoverages: SavePolicyLevelExtraCoveragesInput;
      unitLevelExtraCoverages: SaveUnitLevelExtraCoveragesInput[];
    }) => {
      await dispatch(saveExtraCoverages(payload)).unwrap();
      navigateToPolicyStep(
        navigate,
        NewPolicyRoutes.UNDERWRITING_QUESTIONS,
        NewPolicyRoutes.EXTRA_COVERAGES,
        dispatch
      );
    },
    [dispatch, navigate]
  );

  const onHandleExtraCoveragesSubmitError = useCallback(
    (formValues: ExtraCoveragesForm) => {
      navigate(`.`, {
        state: { stepFormState: formValues },
        replace: true,
      });

      navigateToPolicyStep(
        navigate,
        NewPolicyRoutes.ERROR,
        NewPolicyRoutes.EXTRA_COVERAGES
      );

      setTimeout(() => {
        dispatch(hideLoadingFrame());
      });
    },
    [dispatch, navigate]
  );

  return (
    <ExtraCoverages
      isD2C={false}
      handleExtraCoveragesSubmit={handleExtraCoveragesSubmit}
      onHandleExtraCoveragesSubmitError={onHandleExtraCoveragesSubmitError}
      onHandleExtraCoveragesSubmitStarted={onHandleExtraCoveragesSubmitStarted}
      onLoading={onLoading}
      onLoadingError={onLoadingError}
      onLoadingFinished={onLoadingFinished}
    />
  );
}
